/**
 * Spatial Processing Utilities
 *
 * Functions for creating and processing spatial buckets from telemetry data.
 * Spatial buckets group telemetry points by distance intervals for spatial
 * analysis independent of time-based sampling rate.
 */

import { ProcessedTelemetryData } from '../../domain/telemetry/processed/ProcessedTelemetryData'
import { SpatialBucket } from '../../domain/telemetry/spatial/SpatialBucket'
import { GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import { KMH_PER_MPS } from '../../utils/speedUnits'
import { MS_PER_S } from '../../utils/timeUnits'

/** Default distance bucket size in meters */
export const DISTANCE_BUCKET_SIZE_METERS = 5

/**
 * Temporary bucket structure during processing
 */
interface TelemetryBucket {
  distance: number
  gpsIndex: number
  speed: number
  elapsedMs: number
}

/**
 * Create spatial buckets from telemetry data
 *
 * OPTIMIZED: Uses pre-calculated lean angles instead of recalculating from GPS
 *
 * @param telemetry - Processed telemetry data with aligned arrays
 * @param bucketSizeMeters - Distance interval for each bucket (default 5m)
 * @returns Array of spatial buckets with smoothed metrics
 */
export function createSpatialBuckets(
  telemetry: ProcessedTelemetryData,
  bucketSizeMeters: number = DISTANCE_BUCKET_SIZE_METERS
): SpatialBucket[] {
  const { gpsPoints, leanAngle } = telemetry

  if (gpsPoints.length === 0) return []

  // Step 1: Create distance buckets
  const buckets = bucketTelemetryByDistance(gpsPoints, bucketSizeMeters)

  // Step 2: Calculate raw accelerations between consecutive buckets
  const rawAccelerations = calculateBucketAccelerations(buckets, gpsPoints)

  // Step 3: Apply multi-pass Gaussian smoothing to accelerations
  const smoothedAccelerations = smoothAcceleration(rawAccelerations)

  // Step 4: Extract lean angles from pre-calculated data (FAST - no recalculation)
  // The lean angles were already calculated in RawTelemetryProcessingStage
  const smoothedLeanAngles = buckets.map(
    (bucket) => leanAngle[bucket.gpsIndex]?.leanAngleDegrees ?? 0
  )

  // Step 5: Combine into SpatialBucket objects
  return buckets.map((bucket, index) => ({
    distance: bucket.distance,
    gpsIndex: bucket.gpsIndex,
    elapsedMs: bucket.elapsedMs,
    speedKmh: bucket.speed,
    smoothedLeanAngle: smoothedLeanAngles[index] ?? 0,
    rawLeanAngle: leanAngle[bucket.gpsIndex]?.leanAngleDegrees,
    smoothedAcceleration: index > 0 ? smoothedAccelerations[index - 1] : 0,
    rawAcceleration: index > 0 ? rawAccelerations[index - 1] : 0,
  }))
}

/**
 * Bucket telemetry by distance intervals
 *
 * Groups GPS points into spatial buckets at fixed distance intervals.
 * Each bucket contains the GPS point closest to the bucket's center.
 */
function bucketTelemetryByDistance(
  gpsPoints: GpsPoint[],
  bucketSizeMeters: number
): TelemetryBucket[] {
  const buckets = new Map<number, TelemetryBucket>()

  gpsPoints.forEach((gps, index) => {
    // Determine bucket key (round to nearest bucket size)
    const bucketKey =
      Math.floor(gps.distanceMeters / bucketSizeMeters) * bucketSizeMeters

    // Create bucket if it doesn't exist, or update if this point is closer to bucket center
    const targetDistance = bucketKey + bucketSizeMeters / 2
    const currentDistance = Math.abs(gps.distanceMeters - targetDistance)

    const existing = buckets.get(bucketKey)
    if (
      !existing ||
      currentDistance < Math.abs(existing.distance - targetDistance)
    ) {
      buckets.set(bucketKey, {
        distance: bucketKey,
        gpsIndex: index,
        speed: gps.speedKmh,
        elapsedMs: gps.elapsedMs,
      })
    }
  })

  // Convert to sorted array
  return Array.from(buckets.values()).sort((a, b) => a.distance - b.distance)
}

/**
 * Calculate acceleration between consecutive buckets
 *
 * This calculates acceleration at the same spatial frequency as the buckets
 * (e.g., every 5m) rather than at every GPS point, which matches the
 * original coasting detection implementation.
 */
function calculateBucketAccelerations(
  buckets: TelemetryBucket[],
  gpsPoints: GpsPoint[]
): number[] {
  const accelerations: number[] = []

  for (let i = 1; i < buckets.length; i++) {
    const currentBucket = buckets[i]
    const previousBucket = buckets[i - 1]

    const currentGps = gpsPoints[currentBucket.gpsIndex]
    const previousGps = gpsPoints[previousBucket.gpsIndex]

    // Calculate acceleration between buckets
    const deltaSpeed =
      (currentGps.speedKmh - previousGps.speedKmh) / KMH_PER_MPS
    const deltaTime =
      (currentGps.timestamp.getTime() - previousGps.timestamp.getTime()) /
      MS_PER_S

    const acceleration = deltaTime > 0 ? deltaSpeed / deltaTime : 0
    accelerations.push(acceleration)
  }

  return accelerations
}

/**
 * Apply multi-pass Gaussian smoothing to acceleration data
 */
function smoothAcceleration(
  accelerations: number[],
  passes: number = 3,
  windowSize: number = 5
): number[] {
  let smoothed = accelerations

  for (let pass = 0; pass < passes; pass++) {
    smoothed = applySinglePassGaussianSmoothing(smoothed, windowSize)
  }

  return smoothed
}

/**
 * Generate Gaussian weights for smoothing
 */
function generateGaussianWeights(size: number): number[] {
  const sigma = size / 4
  const weights: number[] = []
  const center = (size - 1) / 2

  for (let i = 0; i < size; i++) {
    const x = i - center
    weights.push(Math.exp(-(x * x) / (2 * sigma * sigma)))
  }

  return weights
}

/**
 * Apply single pass of Gaussian smoothing
 */
function applySinglePassGaussianSmoothing(
  values: number[],
  windowSize: number
): number[] {
  if (values.length === 0) return []

  const halfWindow = Math.floor(windowSize / 2)
  const weights = generateGaussianWeights(windowSize)

  return values.map((_, index) => {
    const start = Math.max(0, index - halfWindow)
    const end = Math.min(values.length, index + halfWindow + 1)

    let sum = 0
    let weightSum = 0

    for (let i = start; i < end; i++) {
      const weightIndex = i - start
      const weight = weightIndex < weights.length ? weights[weightIndex] : 1
      sum += values[i] * weight
      weightSum += weight
    }

    return sum / weightSum
  })
}
